import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AdPreviewContext from "../../../../context/AdPreviewContext";
import axios from "axios";
import { backend } from "../../../../backend";
import { logAxiosError } from "../../../../logging/error";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import { useCallbackPrompt } from "../../../../context/useCallbackPrompt";
import { Helmet } from "react-helmet-async";
import Carousel from "../../../Ad/sections/Carousel/Carousel";
import SimplifiedBooking from "../../../Ad/sections/SimplifiedBooking/SimplifiedBooking";
import Heading from "../../../Ad/sections/Heading/Heading";
import Info from "../../../Ad/sections/Info/Info";
import Description from "../../../Ad/sections/Description/Description";
import Facilities from "../../../Ad/sections/Facilities/Facilities";
import RoundedButton from "../../../../components/Buttons/RoundedButton/RoundedButton";
import "./adPreview.css";

function AdPreview(props) {
  // eslint-disable-next-line no-unused-vars
  const [coordinates, setCoordinates] = useState(null);

  // Set to true when going back to form, this triggers
  // useEffect that disables prompt and navigates back
  const [goBack, setGoBack] = useState(false);

  // Warn users when leaving that their ad is not saved
  // enable and disable the prompt with this state
  const [showPrompt, setShowPrompt] = useState(false);
  const {
    showPrompt: showWarningModal,
    confirmNavigation,
    cancelNavigation,
  } = useCallbackPrompt(showPrompt);

  const {
    adPreviewInfo: { info, hasChanged },
    setAdPreviewInfo,
  } = useContext(AdPreviewContext);

  const location = useLocation();
  const navigate = useNavigate();

  // Säker hantering av info objekt
  const adInfo = info || {};
  const hasValidAdInfo = adInfo && Object.keys(adInfo).length > 0;

  const getCoordinates = useCallback(async () => {
    // Kontrollera att info finns innan API-anrop
    if (!hasValidAdInfo) return;

    try {
      const { data } = await axios.get(backend("/api/utilities/geolocate"), {
        params: {
          address: adInfo?.address,
          city: adInfo?.city,
          zip_code: adInfo?.zip_code,
        },
      });

      if (data.status) {
        setCoordinates({
          lat: data.latitude,
          lng: data.longitude,
        });
      }
    } catch (error) {
      logAxiosError(error);
    }
  }, [hasValidAdInfo, adInfo?.address, adInfo?.city, adInfo?.zip_code]);

  useEffect(() => {
    setShowPrompt(hasChanged);
    getCoordinates();
  }, [hasChanged, getCoordinates]);

  useEffect(() => {
    if (!goBack || showPrompt) return;
    navigate(location.state.prevRoute, { state: { from: "preview" } });
  }, [goBack, showPrompt, navigate, location.state?.prevRoute]);

  const navigateBackHandler = () => {
    setShowPrompt(false);
    setGoBack(true);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Early return om ingen giltig ad info finns
  if (!hasValidAdInfo) {
    return (
      <>
        <Helmet>
          <title>Förhandsvisning</title>
        </Helmet>
        <div className="ad-preview-fallback">
          {/* Navigation header */}
          <div className="ad-preview-fallback-nav">
            <RoundedButton
              size={"sm"}
              color={"green"}
              label={"Tillbaka"}
              onClick={() => navigate("/mina-sidor/annonser")}
            />
            <p>*Ingen förhandsvisning tillgänglig</p>
          </div>

          {/* Centered content */}
          <div className="ad-preview-fallback-content">
            <svg
              className="ad-preview-fallback-icon"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
              <path d="M5 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
            </svg>
            <h2 className="ad-preview-fallback-title">
              Förhandsvisning ej tillgänglig
            </h2>
            <p className="ad-preview-fallback-text">
              Förhandsvisningsdata saknas. Detta kan hända efter en
              siduppdatering. Vänligen gå tillbaka och försök igen.
            </p>
            <RoundedButton
              size={"lg"}
              color={"green"}
              label={"Gå till mina annonser"}
              onClick={() => navigate("/mina-sidor/annonser")}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Förhandsvisning</title>
      </Helmet>
      <div className="ad-component">
        <div id="ad-preview-nav">
          <RoundedButton
            size={"sm"}
            color={"green"}
            label={"Tillbaka"}
            onClick={navigateBackHandler}
          />
          <p>
            <i>*Det här är en förhandsvy av din annons</i>
          </p>
        </div>
        <div id="ad-page-preview-body">
          <>
            <Carousel ad={adInfo} />
            <SimplifiedBooking ad={adInfo} />
            <div id="ad-page-info-prev">
              <Heading ad={adInfo} />
              <Info ad={adInfo} />
              <Description ad={adInfo} />
              <Facilities ad={adInfo} />
            </div>
            <div className="watermark">förhandsvy</div>
          </>
        </div>
      </div>
      {showWarningModal && (
        <WarningModal
          heading="Lämna utan att spara"
          text="Är du säker att du vill lämna sidan? Din annons kommer ej att sparas."
          confirmBtnText="Lämna"
          onCancel={cancelNavigation}
          onConfirm={() => {
            setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
            confirmNavigation();
          }}
        />
      )}
    </>
  );
}

export default AdPreview;
